import { boundMethod } from 'autobind-decorator';
import { Country } from 'lib-shared-legacy-types';
import { ComponentType, PureComponent } from 'react';


import COUNTRIES from './countries.json';
export interface WithCountriesInjectedProps {
  countries: {
    /** Loaded countries on current page */
    items: Array<Country>;

    /** Action to get countries. */
    getItems: () => void;

    /** The search term used to filter countries */
    searchTerm: string;

    /**
     * Handle search term change.
     *
     * @param {string} searchTerm - The search term
     */
    searchTermChange: (searchTerm?: string) => void;
  };
}

type WithCountriesNotInjectedProps<T> = Omit<
  T,
  keyof WithCountriesInjectedProps
>;

type WithCountriesProps<T> = WithCountriesNotInjectedProps<T>;

interface WithCountriesState {
  searchTerm: string;
  items: Array<Country>;
}

export const withCountries = <T extends WithCountriesInjectedProps>(
  Component: ComponentType<T>,
) => {
  class WithCountries extends PureComponent<
    WithCountriesProps<T>,
    WithCountriesState
  > {
    constructor(props: WithCountriesProps<T>) {
      super(props);
      this.state = {
        items: COUNTRIES,
        searchTerm: '',
      };
    }

    @boundMethod
    getItems() {
      this.setState({ items: COUNTRIES });
    }

    @boundMethod
    handleSearchTermChange(searchTerm = '') {
      this.setState({ searchTerm }, () => {
        const countriesFiltered = searchTerm.length
          ? COUNTRIES.filter(
              (country) =>
                country.name
                  .toLowerCase()
                  .startsWith(this.state.searchTerm.toLowerCase()) ||
                country.phone.startsWith(this.state.searchTerm),
            )
          : COUNTRIES;
        this.setState({ items: countriesFiltered });
      });
    }

    @boundMethod
    getInjectedProps(
      props: Readonly<WithCountriesProps<T>>,
      state: Readonly<WithCountriesState>,
    ): WithCountriesInjectedProps['countries'] {
      const { items, searchTerm } = state;

      return this.memoizedGetInjectedProps(searchTerm, items);
    }

    memoizedGetInjectedProps(
      searchTerm: Readonly<WithCountriesState>['searchTerm'],
      items: Readonly<WithCountriesState>['items'],
    ): WithCountriesInjectedProps['countries'] {
      return {
        searchTerm,
        items,
        getItems: this.getItems,
        searchTermChange: this.handleSearchTermChange,
      };
    }

    render() {
      return (
        <Component
          {...(this.props as T)}
          countries={this.getInjectedProps(this.props, this.state)}
        />
      );
    }
  }

  return WithCountries;
};
